import { shopPrefix } from '../../config';
import { ListContentSource, SOURCE_COLLECTION_BY_GENRE } from '../../store/constants';
import { globalErrorResponse, showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { AgeCategory } from '../../../models/AgeCategory';
import { http } from '../axios';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { LoanFormatState } from '../../store/loanFormat/state';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { nothingSelected } from '../../shared/product-selection/ProductSelectionUtils';

type Props = {
    source: ListContentSource;
    ageCategory: AgeCategory;
    filter: string;
    productSelection: ProductSelection;
    genre?: string;
    loanFormat: LoanFormatState;
    onSuccess: () => void;
};

export function useAddProductsToGroupFromCollection({
    source,
    filter,
    ageCategory,
    productSelection,
    genre,
    loanFormat,
    onSuccess
}: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        addToGroupBulk: (productGroupId: number) => {
            const endpoint = `${shopPrefix}/v1/librarymanaged/productgroups/:productGroupId/bulk/:source/:kind/copy`;
            setLoading(true);

            if (nothingSelected(productSelection)) {
                throw new Error(t('notifications.nothingSelected.error'));
            }

            let requestdata = {
                pathParams: {},
                data: {}
            };

            try {
                requestdata = {
                    pathParams: {
                        productGroupId,
                        source
                    },
                    data: {
                        ...requestdata.data,
                        amount: 1,
                        source,
                        sourceId: source === SOURCE_COLLECTION_BY_GENRE ? genre : undefined
                    }
                };

                if (isIncludeProductSelection(productSelection)) {
                    requestdata = {
                        ...requestdata,
                        pathParams: { ...requestdata.pathParams, kind: 'ids' },
                        data: {
                            ...requestdata.data,
                            lineItemKeys: productSelection.included
                        }
                    };
                } else if (isExcludeProductSelection(productSelection)) {
                    requestdata = {
                        ...requestdata,
                        pathParams: { ...requestdata.pathParams, kind: 'filter' },
                        data: {
                            ...requestdata.data,
                            filterRequest: deserializeFilter(filter),
                            excludedLineItemKeys: productSelection.excluded,
                            loanFormat: loanFormat.requestKey
                        }
                    };
                }
            } catch (error) {
                dispatch(showNotification(t('notifications.addProductsToGroup.warning'), TYPE_WARNING));
                return;
            }

            const params = {
                ageCategory: ageCategory === AgeCategory.ALL_AGES || !ageCategory ? undefined : ageCategory
            };

            http.put(endpoint, requestdata.data, { pathParams: requestdata.pathParams, params })
                .then(() => {
                    onSuccess();
                    dispatch(showNotification(t('notifications.addProductsToGroup.success')));
                })
                .catch(error => dispatch(globalErrorResponse(error)))
                .finally(() => {
                    setLoading(false);
                });
        }
    };
}
